import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import type { Slot } from '@/features/scheduling';
import { clockTime } from '@/features/scheduling';
import { dateTag } from '@/lib/i18n';

import { isValidPhone } from './phone';
import type { PublicAddon, PublicService, PublicStaff } from './schemas';

/**
 * §6's client-app components: `ServiceGrid`, `AddonChips`, `TechnicianPicker`, `DateStrip`,
 * `SlotChips`, `PhoneInput`, `OtpInput`, `ConsentCheckbox`, `Summary` and `PolicyNote`.
 *
 * All presentational: every one takes its value and its callback, so the journey's state
 * lives in one place (`BookingJourney`) and each step can be rendered on its own in a test
 * and on a 390 px screen.
 */

/** Integer paise as rupees, the way every 26AD surface prints money (§3 Money). */
export function rupees(paise: number): string {
  return (paise / 100).toFixed(2);
}

/** The running total of a basket, in minutes and paise. */
export function basketTotals(
  services: readonly PublicService[],
  addons: readonly PublicAddon[],
): { minutes: number; paise: number } {
  const all = [...services, ...addons];
  return {
    minutes: all.reduce((sum, each) => sum + each.duration_min, 0),
    paise: all.reduce((sum, each) => sum + each.price_paise, 0),
  };
}

/** §6's `ServiceGrid`: what is bookable online, withdrawn services hidden (BP-02). */
export function ServiceGrid({
  services,
  chosen,
  onToggle,
}: {
  services: PublicService[];
  chosen: readonly string[];
  onToggle: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const live = services.filter((service) => service.is_active);

  if (live.length === 0) {
    return (
      <p role="status" data-testid="no-services">
        {t('services.none')}
      </p>
    );
  }
  return (
    <ul
      data-testid="service-grid"
      className="grid grid-cols-1 gap-2 @lg:grid-cols-2 @3xl:grid-cols-3 @6xl:grid-cols-4"
    >
      {live.map((service) => (
        <li key={service.id}>
          <button
            type="button"
            aria-pressed={chosen.includes(service.id)}
            data-testid={`book-service-${service.id}`}
            onClick={() => {
              onToggle(service.id);
            }}
            className={`w-full rounded border p-3 text-left ${
              chosen.includes(service.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
            }`}
          >
            <span className="block font-medium">{service.name}</span>
            <span className="block text-sm">
              {t('services.duration', { minutes: service.duration_min })} · ₹
              {rupees(service.price_paise)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The add-ons that belong to the chosen services, or to every service. */
export function addonsFor(
  addons: readonly PublicAddon[],
  serviceIds: readonly string[],
): PublicAddon[] {
  return addons.filter(
    (addon) =>
      addon.is_active && (addon.service_id === null || serviceIds.includes(addon.service_id)),
  );
}

/** §6's `AddonChips`. */
export function AddonChips({
  addons,
  chosen,
  onToggle,
}: {
  addons: PublicAddon[];
  chosen: readonly string[];
  onToggle: (id: string) => void;
}): JSX.Element | null {
  const { t } = useTranslation('booking');
  if (addons.length === 0) {
    return null;
  }
  return (
    <div data-testid="addon-chips">
      <h3 className="text-sm font-medium">{t('services.addons')}</h3>
      <ul className="flex flex-wrap gap-2">
        {addons.map((addon) => (
          <li key={addon.id}>
            <button
              type="button"
              aria-pressed={chosen.includes(addon.id)}
              data-testid={`book-addon-${addon.id}`}
              onClick={() => {
                onToggle(addon.id);
              }}
              className={`rounded-full border px-3 py-1 text-sm ${
                chosen.includes(addon.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
              }`}
            >
              {addon.name} · ₹{rupees(addon.price_paise)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** §6's `TechnicianPicker`: "any / specific". `null` is R11d's any-staff merge. */
export function TechnicianPicker({
  staff,
  chosen,
  onChoose,
}: {
  staff: PublicStaff[];
  chosen: string | null;
  onChoose: (id: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const bookable = staff.filter((row) => row.is_bookable && row.active);

  return (
    <ul
      data-testid="technician-picker"
      className="grid grid-cols-1 gap-2 @xl:grid-cols-2 @4xl:grid-cols-3"
    >
      <li>
        <button
          type="button"
          aria-pressed={chosen === null}
          data-testid="technician-any"
          onClick={() => {
            onChoose(null);
          }}
          className={`w-full rounded border p-3 text-left ${
            chosen === null ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
          }`}
        >
          <span className="block font-medium">{t('technician.any')}</span>
          <span className="block text-sm">{t('technician.anyHint')}</span>
        </button>
      </li>
      {bookable.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            aria-pressed={chosen === row.id}
            data-testid={`technician-${row.id}`}
            onClick={() => {
              onChoose(row.id);
            }}
            className={`flex w-full items-center gap-2 rounded border p-3 text-left ${
              chosen === row.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
            }`}
          >
            <span
              aria-hidden="true"
              style={{ background: row.colour }}
              className="inline-block h-6 w-6 rounded-full"
            />
            {row.display_name}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * `YYYY-MM-DD` as a short day label in the tenant's timezone, in the reader's
 * language (BP-19 §2).
 *
 * The one date on the client journey that is *prose* rather than data, so it is the
 * one that follows the switcher. `en` still formats with `en-GB`, so an English
 * screen reads exactly what it read before BP-19.
 */
export function dayLabel(date: string, timezone: string): string {
  return new Intl.DateTimeFormat(dateTag(), {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${date}T12:00:00Z`));
}

/** `count` days from `from`, inclusive, as `YYYY-MM-DD`. */
export function dateStrip(from: string, count: number): string[] {
  const days: string[] = [];
  for (let step = 0; step < count; step += 1) {
    const moved = new Date(`${from}T00:00:00Z`);
    moved.setUTCDate(moved.getUTCDate() + step);
    days.push(moved.toISOString().slice(0, 10));
  }
  return days;
}

/** §6's `DateStrip`. */
export function DateStrip({
  days,
  chosen,
  timezone,
  onChoose,
}: {
  days: string[];
  chosen: string;
  timezone: string;
  onChoose: (date: string) => void;
}): JSX.Element {
  return (
    <ul data-testid="date-strip" className="flex gap-2 overflow-x-auto">
      {days.map((day) => (
        <li key={day}>
          <button
            type="button"
            aria-pressed={day === chosen}
            data-testid={`date-${day}`}
            onClick={() => {
              onChoose(day);
            }}
            className={`whitespace-nowrap rounded border px-3 py-2 ${
              day === chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
            }`}
          >
            {dayLabel(day, timezone)}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** §6's `SlotChips`, with the "no slots → suggest next days" state. */
export function SlotChips({
  slots,
  timezone,
  chosen,
  nextFree,
  onChoose,
}: {
  slots: Slot[];
  timezone: string;
  chosen: Slot | null;
  nextFree: string | null;
  onChoose: (slot: Slot) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');

  if (slots.length === 0) {
    return (
      <div role="status" data-testid="no-slots">
        <p>{t('slot.none')}</p>
        {nextFree === null ? null : (
          <p data-testid="next-free">
            {t('slot.suggestion', { date: dayLabel(nextFree, timezone) })}
          </p>
        )}
      </div>
    );
  }
  return (
    <ul data-testid="slot-chips" className="flex flex-wrap gap-2">
      {slots.map((slot) => {
        const key = `${slot.start_at}|${slot.staff_id}`;
        const active =
          chosen !== null && chosen.start_at === slot.start_at && chosen.staff_id === slot.staff_id;
        return (
          <li key={key}>
            <button
              type="button"
              aria-pressed={active}
              data-testid={`book-slot-${key}`}
              onClick={() => {
                onChoose(slot);
              }}
              className={`rounded border px-3 py-2 tabular-nums ${
                active ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
              }`}
            >
              {clockTime(slot.start_at, timezone)}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** §6's `PhoneInput`: an E.164 IN keypad that says so before the api has to. */
export function PhoneInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const invalid = value !== '' && !isValidPhone(value);
  return (
    <label className="block">
      <span className="text-sm">{t('verify.phone')}</span>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        aria-invalid={invalid}
        data-testid="phone-input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="mt-1 w-full rounded border border-border px-3 py-2 text-lg"
      />
      <span className="text-xs text-muted-foreground">{t('verify.phoneHint')}</span>
      {invalid ? (
        <span role="alert" data-testid="phone-invalid" className="block text-sm text-destructive">
          {t('verify.phoneInvalid')}
        </span>
      ) : null}
    </label>
  );
}

/** §6's `OtpInput`. */
export function OtpInput({
  value,
  length,
  onChange,
}: {
  value: string;
  length: number;
  onChange: (next: string) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  return (
    <label className="block">
      <span className="text-sm">{t('verify.code')}</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        data-testid="otp-input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value.replace(/\D/g, '').slice(0, length));
        }}
        className="mt-1 w-full rounded border border-border px-3 py-2 text-2xl tracking-widest"
      />
    </label>
  );
}

/** §6's `ConsentCheckbox`, with BP-03's notice link beside it (R15). */
export function ConsentCheckbox({
  checked,
  noticeHref,
  onChange,
}: {
  checked: boolean;
  noticeHref: string;
  onChange: (next: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  return (
    <div data-testid="consent-checkbox">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="consent-reminders"
          checked={checked}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <span>
          {t('verify.consent')}
          <span className="block text-xs text-muted-foreground">{t('verify.consentHint')}</span>
        </span>
      </label>
      <a data-testid="notice-link" href={noticeHref} className="text-sm underline">
        {t('verify.notice')}
      </a>
    </div>
  );
}

/** §6's `Summary`. */
export function Summary({
  services,
  addons,
  startAt,
  timezone,
}: {
  services: PublicService[];
  addons: PublicAddon[];
  startAt: string;
  timezone: string;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const totals = basketTotals(services, addons);
  return (
    <div data-testid="summary" className="rounded border border-border p-3">
      <h3 className="font-medium">{t('confirm.summary')}</h3>
      <ul className="text-sm">
        {[...services, ...addons].map((each) => (
          <li key={each.id} data-testid={`summary-${each.id}`}>
            {each.name} · ₹{rupees(each.price_paise)}
          </li>
        ))}
      </ul>
      <p data-testid="summary-when" className="tabular-nums">
        {dayLabel(startAt.slice(0, 10), timezone)} · {clockTime(startAt, timezone)}
      </p>
      <p data-testid="summary-total">
        {t('confirm.total')}: ₹{rupees(totals.paise)} ·{' '}
        {t('services.duration', { minutes: totals.minutes })}
      </p>
    </div>
  );
}

/** §6's `PolicyNote`: R6's cut-off, in the client's own words. */
export function PolicyNote({ cutoffHours }: { cutoffHours: number }): JSX.Element {
  const { t } = useTranslation('booking');
  return (
    <p data-testid="policy-note" className="text-sm text-muted-foreground">
      {t('confirm.policy', { hours: cutoffHours })}
    </p>
  );
}
